'use client'

import * as React from 'react'
import { cn } from '@/lib/tailwind'
import type { Icon } from '@phosphor-icons/react'
import { COMPACT_CLASSES, ICON_SIZES, FOCUS_STYLES } from '@/ui/design-tokens'

interface PropertyBadgeProps {
  Icon: Icon
  label: string
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  className?: string
  children?: React.ReactNode
}

/**
 * A clickable badge that represents a property field.
 * When inactive, shows as an outline badge that can be clicked to activate.
 * When active, shows as a filled badge (usually with a select dropdown).
 */
export function PropertyBadge({
  Icon: IconComponent,
  label,
  onClick,
  active = false,
  disabled = false,
  className,
  children,
}: PropertyBadgeProps) {
  const Component = onClick && !disabled ? 'button' : 'div'

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        COMPACT_CLASSES.base,
        COMPACT_CLASSES.layout,
        FOCUS_STYLES.ringCompact,
        active
          ? COMPACT_CLASSES.active
          : [COMPACT_CLASSES.inactive, COMPACT_CLASSES.inactiveHover],
        disabled && 'cursor-not-allowed opacity-50',
        onClick && !disabled && !active && 'cursor-pointer',
        className,
      )}
    >
      <IconComponent
        className={cn(ICON_SIZES.compact, 'shrink-0')}
        weight={active ? 'bold' : 'regular'}
      />
      {children ?? <span className="truncate">{label}</span>}
    </Component>
  )
}
